import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserType, USER_TYPES, VIEW_MODES } from '../utils/userTypeContext';
import { useAuthState } from '../hooks/useAuthState';
import { useTranslation } from '../utils/i18n';
import { getApiClient } from '../utils/apiClient';
import { Icons } from './Icons';
import TechnicalGuideModal from './TechnicalGuideModal/TechnicalGuideModal';
import logger from '../utils/logger';
import { AUDIENCE, normalizeAudienceParam, userTypeStringToCanonical } from '../utils/audienceParams';
import './UserTypeDropdown.css';

const UserTypeDropdown = ({ isOpen: controlledIsOpen, onToggle }) => {
  const { userType, setUserType, viewMode, setViewMode, isLoading, isEndUser } = useUserType();
  const { user } = useAuthState();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showTechnicalGuide, setShowTechnicalGuide] = useState(false);
  const [isCheckingGuide, setIsCheckingGuide] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = onToggle || setInternalIsOpen;

  // Determine if user is authenticated
  const isAuthenticated = !!user;
  
  // Check if we're in public preview mode
  const isPublicPreview = searchParams.get('view') === 'public';
  
  // When in preview mode AND authenticated, show Partner/Customer options instead of Technical/Customer
  const showUnauthenticatedOptions = !isAuthenticated || isPublicPreview;

  // Sync userType with URL audience parameter when in public preview or unauthenticated
  useEffect(() => {
    if (showUnauthenticatedOptions) {
      const audienceParam = normalizeAudienceParam(searchParams.get('audience'));
      if (audienceParam === AUDIENCE.PLATFORM && userType !== USER_TYPES.PARTNER) {
        setUserType(USER_TYPES.PARTNER);
      } else if (audienceParam === AUDIENCE.GOALS && userType !== USER_TYPES.INVESTORS_REGULATORS) {
        setUserType(USER_TYPES.INVESTORS_REGULATORS);
      } else if (audienceParam === AUDIENCE.BOT && userType !== USER_TYPES.CUSTOMER) {
        setUserType(USER_TYPES.CUSTOMER);
      }
    }
  }, [searchParams, showUnauthenticatedOptions, userType, setUserType]);
  
  // For end users, always force viewMode to NORMAL and prevent dropdown from opening
  useEffect(() => {
    if (isEndUser && viewMode !== VIEW_MODES.NORMAL) {
      setViewMode(VIEW_MODES.NORMAL);
    }
  }, [isEndUser, viewMode, setViewMode]);
  
  // Prevent dropdown from opening for end users
  const handleToggle = () => {
    if (isEndUser && isAuthenticated) {
      // Don't allow opening dropdown for end users
      return;
    }
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  const handleUserTypeChange = (newUserType) => {
    setUserType(newUserType);
    
    // Update URL parameter when in public preview or unauthenticated
    if (showUnauthenticatedOptions) {
      const newParams = new URLSearchParams(searchParams);
      const audienceValue = userTypeStringToCanonical(newUserType);
      newParams.set('audience', audienceValue);
      setSearchParams(newParams);
      
      // Save to localStorage
      localStorage.setItem('audiencePreference', audienceValue);
    }
    
    // If in preview mode and authenticated, also update viewMode to match
    if (isPublicPreview && isAuthenticated) {
      // Map userType to viewMode: Partner -> Technical, Customer -> Normal
      if (newUserType === USER_TYPES.PARTNER) {
        setViewMode(VIEW_MODES.TECHNICAL);
      } else {
        setViewMode(VIEW_MODES.NORMAL);
      }
    }
    setIsOpen(false);
  };

  const handleViewModeChange = async (newViewMode) => {
    // Prevent view mode changes for end users
    if (isEndUser) {
      return;
    }

    // If switching to TECHNICAL, check if we need to show the guide
    if (newViewMode === VIEW_MODES.TECHNICAL && isAuthenticated && !isPublicPreview) {
      // Check localStorage first (fast, synchronous)
      const hasSeenGuide = localStorage.getItem('b-esper_technicalguide');
      
      if (hasSeenGuide === 'true') {
        // User has seen the guide, proceed with switch
        proceedWithViewModeChange(newViewMode);
        return;
      }

      // Not in localStorage, check profile
      setIsCheckingGuide(true);
      try {
        const apiClient = getApiClient();
        const profileResponse = await apiClient.getUserProfile();
        
        if (profileResponse && profileResponse.success && profileResponse.data) {
          const userData = profileResponse.data;
          const settings = userData.settings || {};
          const hasSeenTechnicalGuide = settings.has_seen_technical_guide === true;
          
          if (hasSeenTechnicalGuide) {
            // Profile says user has seen guide, update localStorage and proceed
            localStorage.setItem('b-esper_technicalguide', 'true');
            proceedWithViewModeChange(newViewMode);
            return;
          } else {
            // User hasn't seen guide, show it
            setShowTechnicalGuide(true);
            setIsOpen(false);
            setIsCheckingGuide(false);
            return;
          }
        }
      } catch (error) {
        logger.warn('USER-TYPE-DROPDOWN', 'Failed to check user profile for technical guide', { error: error.message });
        // On error, show guide to be safe
        setShowTechnicalGuide(true);
        setIsOpen(false);
        setIsCheckingGuide(false);
        return;
      }
      
      setIsCheckingGuide(false);
    } else {
      // Not switching to TECHNICAL or not authenticated, proceed normally
      proceedWithViewModeChange(newViewMode);
    }
  };

  const proceedWithViewModeChange = (newViewMode) => {
    setViewMode(newViewMode);
    // If in preview mode and authenticated, also update userType to match
    if (isPublicPreview && isAuthenticated) {
      // Map viewMode to userType: Technical -> Partner, Normal -> Customer
      const newUserType = newViewMode === VIEW_MODES.TECHNICAL ? USER_TYPES.PARTNER : USER_TYPES.CUSTOMER;
      setUserType(newUserType);
      
      // Update URL parameter
      const newParams = new URLSearchParams(searchParams);
      const audienceValue = userTypeStringToCanonical(newUserType);
      newParams.set('audience', audienceValue);
      setSearchParams(newParams);
      
      // Save to localStorage
      localStorage.setItem('audiencePreference', audienceValue);
    }
    setIsOpen(false);
  };

  const handleTechnicalGuideGoBack = () => {
    setShowTechnicalGuide(false);
    // Switch back to NORMAL view mode
    proceedWithViewModeChange(VIEW_MODES.NORMAL);
  };

  const handleTechnicalGuideContinue = async () => {
    setShowTechnicalGuide(false);
    
    // Update user profile to mark that user has seen the guide
    try {
      const apiClient = getApiClient();
      // Get current profile to merge settings
      const profileResponse = await apiClient.getUserProfile();
      if (profileResponse && profileResponse.success && profileResponse.data) {
        const currentSettings = profileResponse.data.settings || {};
        // Merge new setting with existing settings
        await apiClient.updateUserProfile({
          settings: {
            ...currentSettings,
            has_seen_technical_guide: true
          }
        });
      } else {
        // If we can't get current profile, just set the setting
        await apiClient.updateUserProfile({
          settings: {
            has_seen_technical_guide: true
          }
        });
      }
    } catch (error) {
      logger.warn('USER-TYPE-DROPDOWN', 'Failed to update user profile for technical guide', { error: error.message });
      // Continue anyway - localStorage will be updated
    }
    
    // Update localStorage
    localStorage.setItem('b-esper_technicalguide', 'true');
    
    // Switch to TECHNICAL view mode
    proceedWithViewModeChange(VIEW_MODES.TECHNICAL);
  };

  const getUserTypeLabel = (type) => {
    switch (type) {
      case USER_TYPES.PARTNER:
        return t('userTypeDropdown.partner') || 'Platform';
      case USER_TYPES.INVESTORS_REGULATORS:
        return t('userTypeDropdown.investorsRegulators') || 'Goals';
      case USER_TYPES.CUSTOMER:
        return t('userTypeDropdown.customer') || 'Bot';
      default:
        return t('userTypeDropdown.customer') || 'Bot';
    }
  };

  const getViewModeLabel = (mode) => {
    switch (mode) {
      case VIEW_MODES.TECHNICAL:
        return t('userTypeDropdown.technical') || 'Technical';
      case VIEW_MODES.NORMAL:
        return t('userTypeDropdown.customer') || 'Bot';
      default:
        return t('userTypeDropdown.customer') || 'Bot';
    }
  };

  const getUserTypeIcon = (type) => {
    switch (type) {
      case USER_TYPES.PARTNER:
        return <Icons.Business size={16} />;
      case USER_TYPES.INVESTORS_REGULATORS:
        return <Icons.Chart size={16} />;
      case USER_TYPES.CUSTOMER:
        return <Icons.User size={16} />;
      default:
        return <Icons.User size={16} />;
    }
  };

  const getViewModeIcon = (mode) => {
    switch (mode) {
      case VIEW_MODES.TECHNICAL:
        return <Icons.Settings size={16} />;
      case VIEW_MODES.NORMAL:
        return <Icons.User size={16} />;
      default:
        return <Icons.User size={16} />;
    }
  };

  if (showUnauthenticatedOptions) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="user-type-dropdown loading">
        <div className="user-type-btn">
          <span className="user-type-icon">
            {getViewModeIcon(viewMode)}
          </span>
          <span className="user-type-label">
            {getViewModeLabel(viewMode)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="user-type-dropdown" ref={dropdownRef}>
        <button
          className={`user-type-btn ${isEndUser && isAuthenticated ? 'disabled' : ''} ${isCheckingGuide ? 'loading' : ''}`}
          onClick={handleToggle}
          title={isEndUser && isAuthenticated ? t('userTypeDropdown.viewModeLocked', 'View mode is locked for end users') : t('userTypeDropdown.selectUserType', 'Select user type')}
          aria-label={isEndUser && isAuthenticated ? t('userTypeDropdown.viewModeLocked', 'View mode is locked for end users') : t('userTypeDropdown.selectUserType', 'Select user type')}
          aria-expanded={isOpen}
          disabled={(isEndUser && isAuthenticated) || isCheckingGuide}
        >
          <span className="user-type-icon">
            {showUnauthenticatedOptions ? getUserTypeIcon(userType) : getViewModeIcon(viewMode)}
          </span>
          <span className="user-type-label">
            {showUnauthenticatedOptions ? getUserTypeLabel(userType) : getViewModeLabel(viewMode)}
          </span>
          {!(isEndUser && isAuthenticated) && !isCheckingGuide && (
            <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
          )}
          {isCheckingGuide && (
            <span className="loading-spinner">⏳</span>
          )}
        </button>
        
        {isOpen && (
          <div className="user-type-dropdown-menu">
            {showUnauthenticatedOptions ? (
              // Unauthenticated OR Public Preview: Platform, Bot, Goals
              <>
                <button
                  className={`user-type-option ${userType === USER_TYPES.PARTNER ? 'selected' : ''}`}
                  onClick={() => handleUserTypeChange(USER_TYPES.PARTNER)}
                >
                  <span className="user-type-icon">
                    <Icons.Business size={16} />
                  </span>
                  <span className="user-type-text">
                    <div className="user-type-title">{t('userTypeDropdown.partner')}</div>
                  </span>
                  {userType === USER_TYPES.PARTNER && (
                    <span className="checkmark">✓</span>
                  )}
                </button>

                <button
                  className={`user-type-option ${userType === USER_TYPES.CUSTOMER ? 'selected' : ''}`}
                  onClick={() => handleUserTypeChange(USER_TYPES.CUSTOMER)}
                >
                  <span className="user-type-icon">
                    <Icons.User size={16} />
                  </span>
                  <span className="user-type-text">
                    <div className="user-type-title">{t('userTypeDropdown.customer')}</div>
                  </span>
                  {userType === USER_TYPES.CUSTOMER && (
                    <span className="checkmark">✓</span>
                  )}
                </button>
                
                <button
                  className={`user-type-option ${userType === USER_TYPES.INVESTORS_REGULATORS ? 'selected' : ''}`}
                  onClick={() => handleUserTypeChange(USER_TYPES.INVESTORS_REGULATORS)}
                >
                  <span className="user-type-icon">
                    <Icons.Chart size={16} />
                  </span>
                  <span className="user-type-text">
                    <div className="user-type-title">{t('userTypeDropdown.investorsRegulators') || 'Goals'}</div>
                  </span>
                  {userType === USER_TYPES.INVESTORS_REGULATORS && (
                    <span className="checkmark">✓</span>
                  )}
                </button>
              </>
            ) : (
              // Authenticated (not in preview): Show Technical/Normal options
              <>
                <button
                  className={`user-type-option ${viewMode === VIEW_MODES.NORMAL ? 'selected' : ''}`}
                  onClick={() => handleViewModeChange(VIEW_MODES.NORMAL)}
                  disabled={isCheckingGuide}
                >
                  <span className="user-type-icon">
                    <Icons.User size={16} />
                  </span>
                  <span className="user-type-text">
                    <div className="user-type-title">{t('userTypeDropdown.customer')}</div>
                  </span>
                  {viewMode === VIEW_MODES.NORMAL && (
                    <span className="checkmark">✓</span>
                  )}
                </button>
                
                <button
                  className={`user-type-option ${viewMode === VIEW_MODES.TECHNICAL ? 'selected' : ''}`}
                  onClick={() => handleViewModeChange(VIEW_MODES.TECHNICAL)}
                  disabled={isCheckingGuide}
                >
                  <span className="user-type-icon">
                    <Icons.Settings size={16} />
                  </span>
                  <span className="user-type-text">
                    <div className="user-type-title">{t('userTypeDropdown.technical')}</div>
                  </span>
                  {viewMode === VIEW_MODES.TECHNICAL && (
                    <span className="checkmark">✓</span>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      
      <TechnicalGuideModal
        isOpen={showTechnicalGuide}
        onGoBack={handleTechnicalGuideGoBack}
        onContinue={handleTechnicalGuideContinue}
      />
    </>
  );
};

export default UserTypeDropdown;
